import { Link } from "@tanstack/react-router";
import { CalendarDays, ArrowRight, Sparkles } from "lucide-react";
import { useToday, addDaysISO } from "@/lib/calendar/today";
import { parseISO, dayOfWeek, calendarDatePath, calendarMonthPath, calendarYearPath } from "@/lib/calendar/dates";
import { useLocale } from "@/i18n/LocaleContext";
import { getDayInfo } from "@/lib/calendar/info";

/**
 * Hero card showing today's full date + tomorrow.
 * Client-only (date depends on user's local clock); SSR renders fallback skeleton.
 */
export function TodayHero({ fallbackISO }: { fallbackISO: string }) {
  const { t, href, locale } = useLocale();
  const todayClient = useToday();
  const todayISO = todayClient ?? fallbackISO;
  const today = parseISO(todayISO)!;
  const tomorrowISO = addDaysISO(todayISO, 1);
  const tomorrow = parseISO(tomorrowISO)!;

  const todayDow = dayOfWeek(today.y, today.m, today.d);
  const tomorrowDow = dayOfWeek(tomorrow.y, tomorrow.m, tomorrow.d);
  const todayMonth = t.common.months[today.m - 1];
  const tomorrowMonth = t.common.months[tomorrow.m - 1];
  const todayDayName = t.common.days[todayDow];
  const tomorrowDayName = t.common.days[tomorrowDow];

  const info = getDayInfo(today.y, today.m, today.d);

  return (
    <section className="rounded-2xl border border-border bg-gradient-to-br from-card to-card/60 p-6 shadow-sm md:p-8">
      <div className="grid gap-6 md:grid-cols-[1.2fr_1fr] md:gap-8">
        {/* Today */}
        <div suppressHydrationWarning>
          <div className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
            <Sparkles className="h-3 w-3" /> {t.common.legendToday}
          </div>
          <div className="mt-3 flex items-baseline gap-3">
            <span className="text-6xl font-bold leading-none text-primary md:text-7xl">{today.d}</span>
            <div className="flex flex-col">
              <span className="text-xl font-semibold leading-tight">{todayMonth}</span>
              <span className="text-sm text-muted-foreground">{today.y}</span>
            </div>
          </div>
          <p className="mt-2 text-base font-medium text-foreground">
            {todayDayName}
            {locale === "id" && (
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                · {info.pasaran}
              </span>
            )}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {info.hijriFormatted}
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link
              to={href(calendarDatePath(today.y, today.m, today.d)) as never}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              <CalendarDays className="h-4 w-4" />
              {t.home.detailTodayDated(todayDayName, today.d, todayMonth, today.y)}
            </Link>
          </div>
        </div>

        {/* Tomorrow + quick links */}
        <div className="space-y-3" suppressHydrationWarning>
          <Link
            to={href(calendarDatePath(tomorrow.y, tomorrow.m, tomorrow.d)) as never}
            className="block rounded-xl border border-border bg-background/60 p-4 transition-colors hover:border-primary"
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {t.home.tomorrowLabel}
              </span>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="text-3xl font-bold leading-none">{tomorrow.d}</span>
              <span className="text-base font-medium">{tomorrowMonth}</span>
              <span className="text-sm text-muted-foreground">{tomorrow.y}</span>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">{tomorrowDayName}</p>
          </Link>
          <div className="grid grid-cols-2 gap-2">
            <Link
              to={href(calendarMonthPath(today.y, today.m)) as never}
              className="rounded-md border border-border bg-background/60 px-3 py-2 text-center text-sm font-medium hover:border-primary hover:text-primary"
            >
              {t.home.monthThisDated(todayMonth, today.y)}
            </Link>
            <Link
              to={href(calendarYearPath(today.y)) as never}
              className="rounded-md border border-border bg-background/60 px-3 py-2 text-center text-sm font-medium hover:border-primary hover:text-primary"
            >
              {t.home.yearThis(today.y)}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}